import React, { useMemo } from "react";
import { format, startOfDay } from "date-fns";
import { Icon } from "@iconify/react";
import { HStack, VStack } from "../utils";
import {
  ExerciseWindowUnit,
  ExitDateBasis,
  ForfeitureSettings,
} from "../../types/EsopPlan";
import {
  computeLastDateToExercise,
  formatExerciseWindow,
  formatExpiryMode,
  getExitRule,
  mapReasonToExitType,
  resolveExitOutcome,
} from "../../utils/forfeitureUtils";

interface ForfeitureSummaryCardProps {
  /** The plan's forfeiture settings */
  forfeitureSettings: ForfeitureSettings;
  /** Separation reason string matching SeparationReasons constant values */
  separationReason: string;
  /** The employee's last working day */
  lastDay?: Date;
  /** The date the employee submitted their resignation, if known */
  resignationDate?: Date | null;
  /** Called when admin wants to override the lastDateToExercise */
  onOverrideLastDate?: (date: Date) => void;
  /** Over-ridden date set by admin */
  overriddenLastDate?: Date;
  /** Plan name for labelling */
  planName?: string;
  /** Employee's date of joining to evaluate tenure conditions */
  dateOfJoining?: Date;
}

export function ForfeitureSummaryCard({
  forfeitureSettings,
  separationReason,
  lastDay,
  resignationDate,
  onOverrideLastDate,
  overriddenLastDate,
  planName,
  dateOfJoining,
}: ForfeitureSummaryCardProps) {
  // Resolve all hooks unconditionally before any early returns
  const exitType = mapReasonToExitType(separationReason);
  const rule = exitType ? getExitRule(forfeitureSettings, exitType) : undefined;

  // Choose the exercise-window anchor date per the plan's exit date basis:
  // resignation date when configured, otherwise last working date.
  const exitDateBasis =
    forfeitureSettings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE;
  const anchorDate =
    exitDateBasis === ExitDateBasis.RESIGNATION_DATE && resignationDate
      ? resignationDate
      : lastDay;

  // Resolve tenure/conditional rules when we have a last day
  const resolved = useMemo(() => {
    if (!exitType || !rule || !lastDay || !anchorDate) return null;
    return resolveExitOutcome(
      separationReason,
      anchorDate,
      {
        dateOfJoining: dateOfJoining ?? lastDay,
        lastDay,
        resignationDate,
      },
      forfeitureSettings
    );
  }, [
    exitType,
    rule,
    lastDay,
    anchorDate,
    dateOfJoining,
    resignationDate,
    separationReason,
    forfeitureSettings,
  ]);

  const computedLastDate = useMemo(() => {
    if (!lastDay || !rule) return undefined;
    if (resolved) return resolved.resolvedExpiryDate;
    if (!rule.vestedOptions.exerciseWindow.neverExpires) {
      return computeLastDateToExercise(
        lastDay,
        forfeitureSettings,
        separationReason,
        resignationDate
      );
    }
    return undefined;
  }, [
    lastDay,
    rule,
    resolved,
    forfeitureSettings,
    separationReason,
    resignationDate,
  ]);

  if (!exitType) return null;

  if (!rule) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
        <HStack className="items-start gap-2">
          <Icon
            icon="fe:warning"
            width={18}
            color="#B45309"
            className="shrink-0 mt-0.5"
          />
          <VStack className="gap-1">
            <span>
              No forfeiture rule configured for{" "}
              <b className="capitalize">
                {separationReason.toLowerCase().replace(/_/g, " ")}
              </b>{" "}
              in {planName ? `plan "${planName}"` : "this plan"}.
            </span>
            <span className="text-amber-700">
              Default treatment: vested and unvested options will both be
              forfeited immediately on exit.
            </span>
          </VStack>
        </HStack>
      </div>
    );
  }

  // Use resolved vested options when available (picks the matching conditional/tenure rule),
  // otherwise fall back to the base rule. Unvested always uses the base rule because
  // conditional rules in the form only control the vested exercise window.
  const vestedOptions = resolved?.vestedOptions ?? rule.vestedOptions;
  const unvestedOptions = rule.unvestedOptions;

  const isConditionalVested = resolved?.appliedRule.vested === "CONDITIONAL";
  const hasMultipleConditionals = (rule.conditionalRules?.length ?? 0) >= 2;

  const effectiveLastDate = overriddenLastDate ?? computedLastDate;
  const isExpiredDate =
    effectiveLastDate && startOfDay(effectiveLastDate) < startOfDay(new Date());

  // Bug 13: zero-day vested window means forfeit immediately on exit
  const forfeitsVestedImmediately =
    !vestedOptions.exerciseWindow.neverExpires &&
    vestedOptions.exerciseWindow.value === 0 &&
    vestedOptions.exerciseWindow.unit === ExerciseWindowUnit.DAYS;

  const hasAcceleration =
    unvestedOptions.acceleration?.enabled &&
    (unvestedOptions.acceleration.percentage ?? 0) > 0;

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4 space-y-4 text-xs">
      {/* Header */}
      <HStack className="items-center gap-2 text-blue-700">
        <Icon icon="mdi:information-outline" width={18} />
        <span className="font-semibold text-sm">
          Forfeiture rules for{" "}
          <span className="capitalize">
            {separationReason.toLowerCase().replace(/_/g, " ")}
          </span>
          {planName && (
            <span className="font-normal text-blue-600"> — {planName}</span>
          )}
        </span>
      </HStack>

      {/* Vested Options summary */}
      <VStack className="gap-1.5">
        <HStack className="items-center gap-2">
          <p className="font-semibold text-gray-700">Vested Options</p>
          {isConditionalVested && (
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-600">
              tenure condition
            </span>
          )}
        </HStack>
        {forfeitsVestedImmediately ? (
          <p className="text-gray-600">
            Vested options are{" "}
            <span className="font-medium">forfeited immediately</span> on exit.
          </p>
        ) : vestedOptions.exerciseWindow.neverExpires ? (
          <p className="text-gray-600">
            Employee can exercise vested options at any time — the window{" "}
            <span className="font-medium">never expires</span>.
          </p>
        ) : (
          <VStack className="gap-1">
            <p className="text-gray-600">
              Employee has{" "}
              <span className="font-medium">
                {formatExerciseWindow(vestedOptions.exerciseWindow)}
              </span>{" "}
              to exercise after their last day.
              {effectiveLastDate && (
                <>
                  {" "}
                  Expiry date:{" "}
                  <span
                    className={`font-semibold ${
                      isExpiredDate ? "text-red-600" : "text-blue-700"
                    }`}
                  >
                    {format(effectiveLastDate, "dd MMM yyyy")}
                  </span>
                  {isExpiredDate && (
                    <span className="ml-1 text-red-500">(expired)</span>
                  )}
                  {overriddenLastDate && computedLastDate && (
                    <span className="ml-1 text-orange-600">
                      (overridden from {format(computedLastDate, "dd MMM yyyy")}
                      )
                    </span>
                  )}
                </>
              )}
            </p>
            <p className="text-gray-500">
              After expiry, options will{" "}
              <span className="font-medium">
                {vestedOptions.onExpiry.toLowerCase()}
              </span>
              .
            </p>
          </VStack>
        )}

        {/* Override date input */}
        {onOverrideLastDate &&
          computedLastDate &&
          !forfeitsVestedImmediately &&
          !vestedOptions.exerciseWindow.neverExpires && (
            <HStack className="items-center gap-3 mt-2">
              <label className="text-gray-500 whitespace-nowrap">
                Override expiry date:
              </label>
              <input
                type="date"
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400"
                value={
                  overriddenLastDate
                    ? format(overriddenLastDate, "yyyy-MM-dd")
                    : format(computedLastDate, "yyyy-MM-dd")
                }
                onChange={(e) => {
                  const d = new Date(e.target.value);
                  if (!Number.isNaN(d.getTime())) onOverrideLastDate(d);
                }}
              />
            </HStack>
          )}
      </VStack>

      {/* Unvested Options summary */}
      <VStack className="gap-1.5 border-t border-blue-200 pt-3">
        <p className="font-semibold text-gray-700">Unvested Options</p>
        {hasAcceleration ? (
          <VStack className="gap-1">
            <p className="text-gray-600">
              <span className="font-medium">
                {unvestedOptions.acceleration!.percentage}%
              </span>{" "}
              of unvested options will vest immediately on exit (acceleration).
            </p>
            {!forfeitsVestedImmediately &&
              !vestedOptions.exerciseWindow.neverExpires && (
                <p className="text-gray-500 italic">
                  Accelerated options become vested and follow the vested
                  options exercise window above.
                </p>
              )}
            <p className="text-gray-500">
              Remaining unvested options are lapsed immediately.
            </p>
          </VStack>
        ) : unvestedOptions.forfeitImmediately ? (
          <p className="text-gray-600">
            All unvested options will be{" "}
            <span className="font-medium">lapsed immediately</span> upon exit.
          </p>
        ) : (
          <p className="text-gray-600">
            Unvested options continue to vest for a grace period of{" "}
            <span className="font-medium">
              {unvestedOptions.gracePeriodDays ?? 0} day
              {(unvestedOptions.gracePeriodDays ?? 0) !== 1 ? "s" : ""}
            </span>{" "}
            after the exit date, then are lapsed.
          </p>
        )}
      </VStack>

      {/* Dual date guard — shown when the basis date + offset is compared */}
      {/* against the other exit date (e.g. resignation date vs last day) */}
      {resolved?.dualDateGuardInfo && (
        <div className="border-t border-blue-200 pt-2 text-gray-500">
          <p>
            {resolved.dualDateGuardInfo.basisLabel} (
            <span className="font-medium text-gray-600">
              {format(resolved.dualDateGuardInfo.basisDate, "dd MMM yyyy")}
            </span>
            ) vs {resolved.dualDateGuardInfo.otherLabel} (
            <span className="font-medium text-gray-600">
              {format(resolved.dualDateGuardInfo.otherDate, "dd MMM yyyy")}
            </span>
            ) —{" "}
            <span className="font-medium text-gray-600">
              {resolved.dualDateGuardInfo.resolution === "EARLIER_DATE"
                ? "earlier"
                : "later"}{" "}
              date used
            </span>
            :{" "}
            <span className="font-semibold text-blue-700">
              {format(resolved.dualDateGuardInfo.effectiveDate, "dd MMM yyyy")}
            </span>{" "}
            (
            {resolved.dualDateGuardInfo.usedOther
              ? resolved.dualDateGuardInfo.otherLabel
              : resolved.dualDateGuardInfo.basisLabel}
            )
          </p>
        </div>
      )}

      {/* Conflict resolution policy — shown when multiple conditionals exist */}
      {hasMultipleConditionals && rule.conditionalResolution && (
        <div className="border-t border-blue-200 pt-2 text-gray-500">
          <p>
            Conflict resolution:{" "}
            <span className="font-medium text-gray-600">
              {rule.conditionalResolution === "EARLIER_DATE"
                ? "Earlier exercise window takes precedence when multiple tenure conditions match"
                : "Later exercise window takes precedence when multiple tenure conditions match"}
            </span>
          </p>
        </div>
      )}

      {/* Exit date basis + Active employee expiry note */}
      <div className="border-t border-blue-200 pt-2 text-gray-400 space-y-1">
        <p>
          Exercise window starts from:{" "}
          <span className="text-gray-500 font-medium">
            {(forfeitureSettings.exitDateBasis ??
              ExitDateBasis.LAST_WORKING_DATE) ===
            ExitDateBasis.LAST_WORKING_DATE
              ? "Last working date"
              : "Resignation date"}
          </span>
        </p>
        <span>
          Active employee expiry:{" "}
          <span className="text-gray-500">
            {formatExpiryMode(forfeitureSettings.activeEmployee)}
          </span>
        </span>
      </div>
    </div>
  );
}

export default ForfeitureSummaryCard;
